/**
 * Wire and state types for cart sync.
 *
 * The wire types mirror the BUILT Lagniappe contract exactly (see
 * docs/CART_SYNC_PLAN.md §5). Two asymmetries are load-bearing and encoded in
 * the type names:
 *
 *   - P1/P3 take the cart key from the QUERY STRING as strings.
 *   - P2 takes it from the JSON BODY as native numbers.
 *
 * Sending the key in the wrong place does not error — it silently reads and
 * writes different rows, which reproduces the exact "cleared cart comes back"
 * bug this feature exists to fix.
 */

/** Ops the server accepts. `expired` is read-only and never sent. */
export const CartOpType = {
  Set: "set",
  Remove: "remove",
  Clear: "clear",
  Replace: "replace",
  Restore: "restore",
  StashHistory: "stashHistory",
} as const;
export type CartOpType = (typeof CartOpType)[keyof typeof CartOpType];

/** Reasons a cart may be tombstoned. Only `user`/`purchase` are sendable. */
export const ClearedReason = {
  User: "user",
  Purchase: "purchase",
  /** Written by the server's 90-day retention sweep. Never sent by a client. */
  Expired: "expired",
} as const;
export type ClearedReason = (typeof ClearedReason)[keyof typeof ClearedReason];

/**
 * Capture-time, display-only metadata. Never authoritative pricing and never
 * replayed into Shopify.
 *
 * Key order matters: the server compares display objects with a raw
 * JSON.stringify, so an inconsistent order manufactures version churn and a
 * history row on every push. Always build these through makeDisplay().
 */
export interface CartDisplay {
  readonly title?: string;
  readonly sku?: string;
  readonly unitPriceCents?: number;
}

/** A line as the server stores and returns it on the internal shape. */
export interface CartOpItem {
  readonly variantId: number;
  readonly warehouseCode: string;
  readonly quantity: number;
  readonly display?: CartDisplay;
}

export type CartOp =
  | ({ readonly op: typeof CartOpType.Set } & CartOpItem)
  | {
      readonly op: typeof CartOpType.Remove;
      readonly variantId: number;
      readonly warehouseCode: string;
    }
  | {
      readonly op: typeof CartOpType.Clear;
      readonly reason: typeof ClearedReason.User | typeof ClearedReason.Purchase;
    }
  | { readonly op: typeof CartOpType.Replace; readonly items: readonly CartOpItem[] }
  | { readonly op: typeof CartOpType.Restore; readonly historyVersion: number }
  | {
      readonly op: typeof CartOpType.StashHistory;
      readonly items: readonly CartOpItem[];
    };

/** A line as P1/P3 return it — Shopify's property encoding, for byte-exact replay. */
export interface PublicCartItem {
  readonly variantId: number;
  readonly quantity: number;
  readonly properties: { readonly _warehouse_code: string };
  readonly display?: CartDisplay;
}

export interface CartHistoryMeta {
  readonly version: number;
  readonly updatedAt: string;
  readonly itemCount: number;
  readonly totalQuantity: number;
  readonly cleared: boolean;
}

/** P1/P2 success body, and P3's minus `history`/`otherDeviceActiveAt`. */
export interface PublicCartState {
  readonly version: number;
  readonly updatedAt: string | null;
  readonly cleared: boolean;
  readonly clearedReason: ClearedReason | null;
  readonly items: readonly PublicCartItem[];
  readonly otherDeviceActiveAt: string | null;
  readonly history: readonly CartHistoryMeta[];
}

/** P2 body. The cart key lives HERE, never in the query string. */
export interface PushBody {
  readonly locationId: number;
  readonly baseVersion: number;
  readonly opBatchId: string;
  readonly deviceId?: string;
  readonly cartToken?: string;
  readonly ops: readonly CartOp[];
}

/** Why a sync cycle produced no usable server state. */
export const SyncFailure = {
  /** Non-2xx, malformed body, timeout, or transport error. */
  Unavailable: "unavailable",
  /** 403 — session ended or signature rejected. Stop for the page lifetime. */
  Forbidden: "forbidden",
  /** 409 on a guarded op. Carries the server's current state. */
  Conflict: "conflict",
  /** 404 — the requested history version is gone. */
  NotFound: "notFound",
  /** 400 — our payload was wrong. A bug; never evidence about cart state. */
  BadRequest: "badRequest",
} as const;
export type SyncFailure = (typeof SyncFailure)[keyof typeof SyncFailure];

export type SyncResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly failure: SyncFailure;
      readonly status?: number;
      readonly current?: PublicCartState;
      readonly detail?: string;
    };

/** Persisted bookkeeping. Split across three keys on purpose — see state.ts. */
export interface SyncState {
  readonly customerKey: string;
  readonly locationId: number;
  readonly lastAppliedVersion: number;
  readonly lastAppliedHash: string;
  readonly lastPushedCartToken: string | null;
  /** Keys that repeatedly fail to replay locally (deleted/unpublished variants). */
  readonly unavailableLines: readonly string[];
}

/**
 * Evidence that the customer deliberately emptied their cart.
 *
 * Version-bound and short-lived: without both, one stuck flag permanently
 * disables the empty-local guard, and a cart-cookie expiry days later would be
 * read as a clear and wipe the shared cart.
 */
export interface ClearEvidence {
  readonly at: number;
  readonly reason: typeof ClearedReason.User | typeof ClearedReason.Purchase;
  readonly baseVersion: number;
}

/**
 * A remote apply that started but has not been verified complete.
 *
 * Stores the TARGET, never the operations: replaying stored ops after a crash
 * re-issues adds that already landed, and /cart/add.js increments rather than
 * sets, so every replayed add doubles that line.
 */
export interface ApplyInProgress {
  readonly targetVersion: number;
  readonly targetItems: readonly CartOpItem[];
  readonly attempts: number;
}

export interface SyncGuards {
  readonly clearEvidence: ClearEvidence | null;
  readonly applyInProgress: ApplyInProgress | null;
  /**
   * When this device first saw an empty local cart it could not explain.
   *
   * A purchase and a lost cart cookie look identical at that instant, and the
   * difference only arrives 5-7 s later as the server's purchase tombstone. So
   * the state-loss verdict waits for that to settle rather than acting on the
   * first observation. Null means "not currently waiting".
   */
  readonly emptyLocalSince: number | null;
}
